import { ColumnType } from "antd/es/table";
import moment from "moment";
import {
  PromotionCodeUsageType,
  PromotionDiscountType,
  PromotionModel,
  PromotionStatus,
} from "../../../../types/model/promotion";
import { DATETIME_FORMAT_WITH_MINUTE, EMPTY } from "../../../../constants/common";
import CustomerDot from "../../../../components/CustomDot";
import {
  getBulkPromotionDisplayCode,
  getDisplayPromotionCodeUsage,
  getDisplayPromotionDiscountType,
  getDisplayPromotionStatus,
  toTitleCase,
} from "../util";
import { PromotionType } from "../../../../types/dto/response/promotion";

export default function getPromotionTableColumns(): ColumnType<PromotionModel>[] {
  return [
    {
      title: "Promotion name",
      key: "name",
      dataIndex: "name",
      width: 220,
      fixed: "left",
      ellipsis: true,
      render: (text: string) => text,
    },
    {
      title: "Applied base price",
      key: "basePrices",
      width: 220,
      ellipsis: true,
      render: (_, { basePrices, discountType }) => {
        if (discountType === PromotionDiscountType.BUY_ONE_GIFT_ONE) {
          return EMPTY;
        }
        return basePrices?.map((basePrice) => basePrice.name).join(", ");
      },
      sorter: () => 0,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      width: 125,
      ellipsis: true,
      render: (status: PromotionStatus) => {
        return <CustomerDot color={getDisplayPromotionStatus(status)} />;
      },
    },
    {
      title: "Code usage",
      key: "promotionType",
      dataIndex: "promotionType",
      width: 140,
      ellipsis: true,
      render: (text: PromotionCodeUsageType) => getDisplayPromotionCodeUsage(text),
      sorter: () => 0,
      filters: [
        {
          text: toTitleCase(PromotionCodeUsageType.REGULAR),
          value: PromotionType.RECURLY,
        },
        {
          text: toTitleCase(PromotionCodeUsageType.BULK),
          value: PromotionType.BULK,
        },
      ],
    },
    {
      title: "Promotion code",
      key: "code",
      dataIndex: "code",
      width: 200,
      ellipsis: true,
      render: (code: string, { promotionType }) => getBulkPromotionDisplayCode(code, promotionType),
    },
    {
      title: "Promotion Type",
      key: "discountType",
      dataIndex: "discountType",
      width: 170,
      ellipsis: true,
      render: (discountType: PromotionDiscountType) => getDisplayPromotionDiscountType(discountType),
      sorter: () => 0,
      filters: [
        {
          text: getDisplayPromotionDiscountType(PromotionDiscountType.AMOUNT_OFF),
          value: PromotionDiscountType.AMOUNT_OFF,
        },
        {
          text: getDisplayPromotionDiscountType(PromotionDiscountType.BUY_ONE_GIFT_ONE),
          value: PromotionDiscountType.BUY_ONE_GIFT_ONE,
        },
        {
          text: getDisplayPromotionDiscountType(PromotionDiscountType.PERCENTAGE_OFF),
          value: PromotionDiscountType.PERCENTAGE_OFF,
        },
      ],
    },
    {
      title: "Discount",
      key: "discount",
      dataIndex: "discountType",
      width: 120,
      ellipsis: true,
      render: (discountType: PromotionDiscountType, record: PromotionModel) => {
        if (discountType === PromotionDiscountType.PERCENTAGE_OFF && record.percentageDiscount) {
          return `${record.percentageDiscount}%`;
        } else if (discountType === PromotionDiscountType.AMOUNT_OFF && record.amountDiscount && record.currencyCode) {
          return `${record.currencyCode} ${record.amountDiscount}`;
        }
        return EMPTY;
      },
      sorter: () => 0,
    },
    {
      title: "Time range",
      key: "timeRange",
      dataIndex: "startAt",
      width: 312,
      ellipsis: true,
      render: (_value: string, record: PromotionModel) => {
        const localStart = moment(record.startAt).local().format(DATETIME_FORMAT_WITH_MINUTE);
        const localEnd = record.endAt ? moment(record.endAt).local().format(DATETIME_FORMAT_WITH_MINUTE) : EMPTY;
        return `${localStart} to ${localEnd}`;
      },
    },
  ];
}
